import { Event } from '@livecontrol/scheduler/model';
import type { ReactElement } from 'react';
import { useCallback, useState } from 'react';
import styled from 'styled-components';
import { Tag } from '../../components';
import { Player } from '../event-details';
import type { ScheduledEvent } from '../event-details';
import { EventInfo } from './event-info';
import { PlayerBar } from './player-bar';

interface Props {
  scheduledEvent: ScheduledEvent;
  /** Refetches the event; used after its recording is replaced. */
  refetchEvent: () => Promise<unknown>;
}

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  min-width: 0;
`;

const PlayerFrame = styled.div`
  position: relative;
  border-radius: 12px;
  overflow: hidden;

  /*
   * The frame is 3:2 but streams are usually 16:9, which letterboxes (black bars
   * top/bottom). Fill the frame instead, cropping the sides. Fullscreen restores
   * the full picture (contain) via the player's own fullscreen button.
   */
  video {
    object-fit: cover !important;
  }

  .vjs-fullscreen video,
  video.vjs-fullscreen {
    object-fit: contain !important;
  }
`;

const LiveBadge = styled.div`
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 4;

  i {
    color: rgb(53, 211, 153);
  }
`;

export const LeftPanel = ({ scheduledEvent, refetchEvent }: Props): ReactElement => {
  const isLive = scheduledEvent.status === Event.Phase.Live.toString();

  /*
   * The player reads its source once on mount, so a refetch alone leaves the old
   * recording on screen. Bumping the key remounts it against the new URL.
   */
  const [playerKey, setPlayerKey] = useState(0);

  const onAssetReplaced = useCallback((): void => {
    void refetchEvent().then(() => {
      setPlayerKey((value) => value + 1);
    });
  }, [refetchEvent]);

  return (
    <Column>
      <PlayerFrame>
        <Player key={playerKey} scheduledEvent={scheduledEvent} ratio={16 / 9} />
        {isLive && (
          <LiveBadge>
            <Tag
              icon='fas fa-circle'
              iconSize={8}
              style={{
                background: 'rgba(8, 14, 30, 0.7)',
                border: 'none',
                color: 'var(--white)',
                fontWeight: 700,
                fontSize: '11px'
              }}
            >
              LIVE
            </Tag>
          </LiveBadge>
        )}
      </PlayerFrame>
      <PlayerBar scheduledEvent={scheduledEvent} onAssetReplaced={onAssetReplaced} />
      <EventInfo scheduledEvent={scheduledEvent} />
    </Column>
  );
};
